/** @jsxImportSource @emotion/react */
import React  from 'react';
import { css, jsx } from '@emotion/react';
import Table, { Cell, Column, AvatarCell, VisibleOnRowHover } from 'components/table';
import { FormattedMessage, FormattedNumber, FormattedDate } from 'components/i18n';
import {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import Tooltip from '@atlaskit/tooltip';
import { Customer, Sale } from '../types';
import { CompanyAvatar } from '../../../components/avatar';
import { MoreDropdownMenu } from '../../../components/dropdown-menu';
import { TableProps } from '../../../components/table';
import SaleStatus from './SaleStatus';
import { Purchase } from '../../purchase/types';
import { useAuth } from '../../auth';
import { truncateCss } from '../../../components/table/Cell';
import BookingInfoCell from '../../../components/table/BookingInfoCell';

interface Props extends Omit<TableProps<Sale>, 'columns'> {
}

const MoreDropdownCell = React.memo(() => {
  const dropDownItemGroup = React.useMemo(()=> (
    <DropdownItemGroup>
      <DropdownItem><FormattedMessage id="sale.SalesTable.options.view" defaultMessage="View" /></DropdownItem>
      <DropdownItem><FormattedMessage id="sale.SalesTable.options.remove" defaultMessage="Remove" /></DropdownItem>
    </DropdownItemGroup>
  )
  , []);
  return (
    <VisibleOnRowHover>
      <MoreDropdownMenu>
        {dropDownItemGroup}
      </MoreDropdownMenu>
    </VisibleOnRowHover>
  );
});

export const CustomerCell = React.memo(function CustomerCell({customer}: {customer?: Customer}) {
  const avatar = React.useMemo(()=><CompanyAvatar size="medium" company={customer!} />, [customer?.name]);
  return (
    <AvatarCell
      avatar={avatar}
      primaryText={customer!.name}
      secondaryText={customer!.contactName}
    />
  );
});


const SalesTable = (props: Props) => {
  const { isBookkeeper } = useAuth();

  const tableColumns = React.useMemo(() => {

    let columns: Column<Sale>[] =  [
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.invoiceDate" defaultMessage="Invoice date" />,
        accessor: 'invoiceDate',
        width: 100,
        Cell: ({value}) => <FormattedDate value={value} />,
      },
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.customer" defaultMessage="Customer" />,
        accessor: 'customer',
        width: 240,
        disableSortBy: true,
        Cell: ({row: {original: { customer}}}) => <CustomerCell customer={customer} />,
      },
      // {
      //   Header: <FormattedMessage id="sale.SalesTable.headings.vat" defaultMessage="VAT"/>,
      //   accessor: 'vatCodes',
      //   Cell: ({value}) => value && value.join(', '),
      //   width: 100,
      // },
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.invoiceNumber" defaultMessage="Invoice number" />,
        accessor: 'invoiceNumber',
        width: 100,
        Cell: ({value}) => <Cell primaryText={value} />,
      }];

    if (isBookkeeper) {
      columns.push(
        {
          Header: (
            <FormattedMessage
              id="sale.SalesTable.headings.vat"
              defaultMessage="VAT"
            />
          ),
          width: 100,
          disableSortBy: true,
          accessor: 'Lines',
          Cell: ({ value }) =>  {
            let accounts = _.map(_.uniqBy(value, 'account.accountNumber'), 'account');
            accounts = accounts.filter(account => !!account);
            const accountDescriptions = _.map(accounts, 'description').join(', ');
            // console.log('accounts',accounts);
            const accountDescriptionsWithNumber = accounts.map((account) => `${account.accountNumber} - ${account.description}`).join(', ');
            return (
              <div css={truncateCss}>
                <Tooltip content={<span>{accountDescriptionsWithNumber}</span>}>
                  <div>
                    <Cell
                      primaryText={(
                        <span css={css`font-size: 0.9em`}> 
                          {' '}
                          {_.uniq(_.map(value, 'vatCode.id')).join(', ')}
                        </span>
)}
                      secondaryText={<span>{accountDescriptions}</span>}
                    />
                  </div>
                </Tooltip>
              </div>
            );
          }

        },
      );

    }

    columns = columns.concat([
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.total" defaultMessage="Total" />,
        align: 'right',
        accessor: 'totalAmount',
        width: 80,
        Cell: React.memo(({ row: { original: sale } }) => (
          <>
            {sale.currency!=='EUR'&&(
            <span css={css`padding-right: 4px;`}>
              {sale.currency} 
            </span>
            )}
            <FormattedNumber value={sale.totalCurrencyAmount as number} />
          </>
        )),
      },
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.dueDate" defaultMessage="Due date" />,
        accessor: 'dueDate',
        minWidth: 100,
        width: 56,
        align: 'right',
        Cell: ({value}) => <FormattedDate value={value} />,
      },
      {
        Header: <FormattedMessage id="sale.SalesTable.headings.status" defaultMessage="Status" />,
        accessor: 'status',
        Cell: React.memo(({row: {original: sale}}) => <SaleStatus invoice={sale} />),
        width: 100,
        align: 'right'
      },

    // {
    //   id: 'actions',
    //   Header: "",
    //   minWidth: 56,
    //   width: 56,
    //   align: 'right',
    //   noLink: true,
    //   Cell: () => <MoreDropdownCell/>
    // },
    ]);
    if (isBookkeeper) {
      columns.push({
        id: 'user',
        Header: '',
        minWidth: 56,
        width: 56,
        align: 'right',
        noLink: true,
        Cell: ({row: {original: sale}}) => <BookingInfoCell user={sale.user} updated={sale.updated} />
      });
    }
    return columns;

  }, [isBookkeeper]);

  return (
    <Table<Sale>
      name="SalesTable"
      columns={tableColumns}
      manualSortBy
      hasPagination
      skeletonCount={12}
      getRowTo={(sale)=> sale.isImported? `/verkopen/${sale.id}/bewerk` : (sale.id || '')}
      {...props}
    />
  );
};

SalesTable.defaultProps = {
  sales: [],
};

export default React.memo(SalesTable);
